import { extendTheme } from '@chakra-ui/react';

/**
 * Chakra UI Theme Configuration for v1.8.9
 *
 * Defines global styles and component overrides for Chakra UI components.
 * Note: Chakra UI v1 has limited component theming support compared to v2.
 */
export const chakraTheme = extendTheme({
  colors: {
    gray: {
      50: '#F7FAFC',
      100: '#EDF2F7',
      200: '#E2E8F0',
      300: '#CBD5E0',
      400: '#8A95A8',
      500: '#647487',
      600: '#4A5568',
      700: '#2D3748',
      800: '#1A202C',
      900: '#171923',
    },
    pink: {
      50: '#FFF5F7',
      100: '#FED7E2',
      200: '#FBB6CE',
      300: '#F687B3',
      400: '#ED64A6',
      500: '#D53F8C',
      600: '#B83280',
      700: '#97266D',
      800: '#702459',
      900: '#521B41',
    },
    red: {
      50: '#FFF5F5',
      100: '#FED7D7',
      200: '#FEB2B2',
      300: '#FC8181',
      400: '#F56565',
      500: '#E53E3E',
      600: '#C53030',
      700: '#9B2C2C',
      800: '#822727',
      900: '#63171B',
    },
    orange: {
      50: '#FFFAF0',
      100: '#FEEBC8',
      200: '#FBD38D',
      300: '#F6AD55',
      400: '#ED8936',
      500: '#DD6B20',
      600: '#C05621',
      700: '#9C4221',
      800: '#7B341E',
      900: '#652B19',
    },
    yellow: {
      50: '#FFFFF6',
      100: '#FFFBCL',
      200: '#FFF3A6',
      300: '#FFED7F',
      400: '#FFDC00',
      500: '#D69E2E',
      600: '#B7791F',
      700: '#975A16',
      800: '#744210',
      900: '#665000',
    },
    green: {
      50: '#F0FFF4',
      100: '#C6F6D5',
      200: '#9AE6B4',
      300: '#68D391',
      400: '#48BB78',
      500: '#38A169',
      600: '#2C7D54',
      700: '#276749',
      800: '#22543D',
      900: '#1C4532',
    },
    teal: {
      50: '#E6FFFA',
      100: '#B2F5EA',
      200: '#81E6D9',
      300: '#4FD1C5',
      400: '#38B2AC',
      500: '#319795',
      600: '#2C7A7B',
      700: '#285E61',
      800: '#234E52',
      900: '#1D4044',
    },
    cyan: {
      50: '#EDFDFD',
      100: '#C4F1F9',
      200: '#9DECF9',
      300: '#76E4F7',
      400: '#0BC5EA',
      500: '#00B5D8',
      600: '#00A3C4',
      700: '#0987A0',
      800: '#086F83',
      900: '#065666',
    },
    blue: {
      50: '#EBF8FF',
      100: '#BEE3F8',
      200: '#90CDF4',
      300: '#63B3ED',
      400: '#4299E1',
      500: '#3182CE',
      600: '#2B6CB0',
      700: '#2C5282',
      800: '#2A4365',
      900: '#1A365D',
    },
    purple: {
      50: '#FAF5FF',
      100: '#E9D8FD',
      200: '#D6BCFA',
      300: '#B794F4',
      400: '#9F7AEA',
      500: '#805AD5',
      600: '#6B46C1',
      700: '#553C9A',
      800: '#44337A',
      900: '#322659',
    },
  },
  styles: {
    global: {
      // Global tooltip styling using CSS selectors
      '[role="tooltip"]': {
        backgroundColor: '#003380 !important', // gray.700
        color: 'white !important',
        fontSize: '0.875rem !important', // sm
        fontWeight: '500 !important',
        padding: '0.5rem 0.75rem !important',
        borderRadius: '0.375rem !important', // md
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important', // lg
        maxWidth: '320px !important',
      },
      // Style the tooltip arrow
      '[data-popper-arrow]': {
        '--popper-arrow-bg': '#003380 !important',
      },
      // Alternative selector for tooltip content
      '.chakra-tooltip': {
        backgroundColor: '#003380 !important',
        color: 'white !important',
        fontSize: '0.875rem !important',
        fontWeight: '500 !important',
        padding: '0.5rem 0.75rem !important',
        borderRadius: '0.375rem !important',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important',
        maxWidth: '320px !important',
      },
      // Global focus-visible styles for keyboard navigation (WCAG 2.4.7)
      // Double-ring: white inner ring + blue outer shadow.
      // Visible on any background, including blue buttons.
      '*:focus-visible': {
        outline: '2px solid white !important',
        outlineOffset: '1px !important',
        boxShadow: '0 0 0 4px #3182CE !important',
      },
    },
  },
});
